'use client';

import React, { useState, useRef } from 'react';
import { Upload, FileImage, Type, Trash2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

interface Asset {
  id: string;
  name: string;
  type: 'logo' | 'font';
  size: string;
  uploaded: string;
  previewUrl?: string;
}

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (date: Date): string => {
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const ACCEPTED_TYPES = ['image/svg+xml', 'image/png', 'image/jpeg', 'image/webp', 'font/woff2', 'font/woff', 'font/ttf'];
const ACCEPTED_EXTENSIONS = ['.svg', '.png', '.jpg', '.jpeg', '.webp', '.woff2', '.woff', '.ttf'];
const MAX_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB

const isLogoType = (file: File) =>
  ['image/svg+xml', 'image/png', 'image/jpeg', 'image/webp'].includes(file.type) ||
  ['.svg', '.png', '.jpg', '.jpeg', '.webp'].some((ext) => file.name.toLowerCase().endsWith(ext));

export default function BrandVaultAssets() {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processFile = (file: File) => {
    if (file.size > MAX_SIZE_BYTES) {
      toast.error(`"${file.name}" ist zu groß. Maximal 5 MB erlaubt.`);
      return;
    }

    const isLogo = isLogoType(file);
    const newAsset: Asset = {
      id: `asset-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      name: file.name,
      type: isLogo ? 'logo' : 'font',
      size: formatSize(file.size),
      uploaded: formatDate(new Date()),
    };

    if (isLogo) {
      const reader = new FileReader();
      reader.onload = (e) => {
        newAsset.previewUrl = e.target?.result as string;
        setAssets((prev) => [...prev, newAsset]);
        toast.success(`"${file.name}" erfolgreich hochgeladen`);
      };
      reader.readAsDataURL(file);
    } else {
      setAssets((prev) => [...prev, newAsset]);
      toast.success(`"${file.name}" erfolgreich hochgeladen`);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;
    files.forEach(processFile);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    files.forEach(processFile);
    // Reset input so same file can be re-uploaded
    e.target.value = '';
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleUploadKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fileInputRef.current?.click();
    }
  };

  const removeAsset = (id: string, previewUrl?: string) => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setAssets((prev) => prev.filter((a) => a.id !== id));
    toast.success('Asset entfernt');
  };

  return (
    <div className="punk-card p-5">
      <h2 className="text-sm font-700 text-white uppercase tracking-widest mb-4 pb-3 border-b border-border" id="assets-heading">
        Brand Assets
      </h2>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_EXTENSIONS.join(',')}
        multiple
        className="hidden"
        onChange={handleFileChange}
        aria-hidden="true"
        tabIndex={-1}
      />

      {/* Upload Zone */}
      <div
        className={`border-2 border-dashed rounded p-6 text-center cursor-pointer transition-all duration-150 mb-4 focus:outline-none focus:ring-2 focus:ring-accent ${
          dragging ? 'border-accent bg-accent/5' : 'border-border hover:border-accent/50'
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={handleClick}
        onKeyDown={handleUploadKeyDown}
        role="button"
        tabIndex={0}
        aria-label="Brand Asset hochladen — Logo oder Schriftart hierher ziehen oder klicken"
        aria-describedby="upload-hint"
      >
        <Upload size={20} className="text-muted mx-auto mb-2" aria-hidden="true" />
        <p className="text-xs text-muted">Logo oder Schriftart hierher ziehen</p>
        <p className="text-xs text-muted-2 mt-1" id="upload-hint">SVG, PNG, JPG, WebP, WOFF2 · max. 5 MB</p>
      </div>

      {/* Asset List */}
      {assets.length > 0 ? (
        <ul className="space-y-2" aria-label="Hochgeladene Brand Assets" aria-labelledby="assets-heading">
          {assets.map((asset) => (
            <li key={asset.id} className="flex items-center gap-3 p-3 bg-surface border border-border rounded group hover:border-accent/30 transition-colors">
              <div
                className="w-7 h-7 bg-surface-2 rounded flex items-center justify-center flex-shrink-0 overflow-hidden"
                aria-hidden="true"
              >
                {asset.previewUrl ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={asset.previewUrl} alt={asset.name} className="w-full h-full object-contain" />
                ) : asset.type === 'logo' ? (
                  <FileImage size={13} className="text-accent" />
                ) : (
                  <Type size={13} className="text-blue-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-500 text-white truncate">{asset.name}</p>
                <p className="text-xs text-muted mono">{asset.size} · {asset.uploaded}</p>
              </div>
              <CheckCircle
                size={12}
                className="text-green-400 flex-shrink-0"
                aria-label="Asset erfolgreich hochgeladen"
              />
              <button
                onClick={() => removeAsset(asset.id, asset.previewUrl)}
                className="opacity-0 group-hover:opacity-100 text-muted hover:text-red-400 transition-all p-1 focus:opacity-100 focus:outline-none focus:ring-1 focus:ring-red-400 rounded"
                aria-label={`Asset "${asset.name}" entfernen`}
                title="Asset entfernen"
              >
                <Trash2 size={12} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted text-center py-2">Noch keine Assets hochgeladen</p>
      )}
    </div>
  );
}